import React, { useEffect, useState } from "react";
import { calendarApi } from "../api";
import "./AdminCalendar.css";

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await calendarApi.getAll();
      setEvents(res.data || []);
    } catch (err) {
      console.error("Error fetching calendar events:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.startDate || !formData.description || !formData.type) {
      return alert("Title, Start Date, Description, and Event Type are required.");
    }

    setLoading(true);
    try {
      await calendarApi.create(formData);
      alert("✅ Calendar event scheduled successfully!");
      setFormData({ title: "", description: "", startDate: "", endDate: "", type: "" });
      fetchEvents();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Failed to add event.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this calendar event?")) return;
    try {
      await calendarApi.delete(id);
      alert("🗑️ Event removed from calendar!");
      fetchEvents();
    } catch (err) {
      console.error("Delete error:", err);
      alert("❌ Failed to delete event.");
    }
  };

  return (
    <div className="admin-calendar-wrapper">
      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="far fa-calendar-alt title-icon"></i>
          <div>
            <h2>Manage Chapter Calendar</h2>
            <p className="panel-subtitle">Schedule upcoming workshops, hackathons, deadlines, and chapter meetings</p>
          </div>
        </div>
      </div>

      <div className="calendar-layout-grid">
        {/* Schedule Form */}
        <div className="calendar-form-card">
          <h3 className="sub-heading-tag"><i className="fas fa-calendar-plus"></i> Schedule Event</h3>
          <form className="admin-glass-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label>Event Title *</label>
              <input
                type="text"
                name="title"
                placeholder="e.g. ACM Summer Code Jam 2026"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="admin-form-group">
              <label>Event Category / Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Event Type *</option>
                <option value="Hackathon">Hackathon</option>
                <option value="Workshop">Workshop</option>
                <option value="Seminar">Seminar</option>
                <option value="Competition">Competition</option>
                <option value="Meeting">Chapter Meeting</option>
              </select>
            </div>

            <div className="calendar-dates-row">
              <div className="admin-form-group">
                <label><i className="far fa-clock"></i> Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label><i className="far fa-clock"></i> End Date (Optional)</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="admin-form-group">
              <label>Description &amp; Agenda *</label>
              <textarea
                name="description"
                placeholder="Brief summary of schedule and target audience..."
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                required
              />
            </div>

            <button type="submit" className="admin-btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i> Scheduling...
                </>
              ) : (
                <>
                  <i className="fas fa-calendar-check"></i> Add to Calendar
                </>
              )}
            </button>
          </form>
        </div>

        {/* Calendar Events List */}
        <div className="calendar-events-feed">
          <h3 className="sub-heading-tag">
            <i className="fas fa-list-alt"></i> Scheduled Activities ({events.length})
          </h3>

          {events.length === 0 ? (
            <p className="no-items-text">No events currently scheduled in the calendar.</p>
          ) : (
            <div className="calendar-items-list">
              {events.map((ev) => (
                <div key={ev.id} className={`cal-event-card type-${ev.type?.toLowerCase()}`}>
                  <div className="cal-date-badge">
                    <span className="cal-day">
                      {ev.startDate ? new Date(ev.startDate).getDate() : "—"}
                    </span>
                    <span className="cal-mon">
                      {ev.startDate
                        ? new Intl.DateTimeFormat("en-US", { month: "short" }).format(new Date(ev.startDate))
                        : "TBA"}
                    </span>
                  </div>

                  <div className="cal-event-info">
                    <div className="cal-top-meta">
                      <span className="cal-type-tag">{ev.type}</span>
                      {ev.endDate && ev.endDate !== ev.startDate && (
                        <span className="cal-duration-tag">Until {ev.endDate}</span>
                      )}
                    </div>
                    <h4>{ev.title}</h4>
                    <p>{ev.description}</p>
                  </div>

                  <button
                    className="delete-cal-btn"
                    onClick={() => handleDelete(ev.id)}
                    title="Delete event"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;